import { CloseCircleFilled } from "@ant-design/icons";
import moment from "moment";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import useClickOutside from "./../../../app/utils/useClickOutside";
import CustomYearMonthPicker from "./../CustomYearMonthPicker";

const MONTH_NAMES: string[] = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const DAYS: string[] = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
type TCustomDatePicker = {
  setDate: Dispatch<SetStateAction<string>>;
  dateValue: string;
  label?: string;
  width?: string;
  color?: string;
  disabled?: boolean;
  placeHolder?: string;
  border?: boolean;
  height?: string;
  showLeft?: boolean;
  showtoptopicker?: string;
};

const CustomDatePicker = ({
  dateValue,
  setDate,
  label,
  height,
  width,
  color,
  disabled,
  placeHolder,
  showLeft = false,
  border = false,
  showtoptopicker,
}: TCustomDatePicker) => {
  const [showDatepicker, setShowDatepicker] = useState(false);
  const [datepickerValue, setDatepickerValue] = useState("");
  const [month, setMonth] = useState<number>(0);
  const [year, setYear] = useState<number>(0);
  const [no_of_days, setNoOfDays] = useState<number[]>([]);
  const [blankdays, setBlankdays] = useState<number[]>([]);
  const [yearMonth, setYearMonth] = useState<string>("");

  const dateRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (dateValue) {
      const parsed = moment(dateValue, "YYYY-MM-DD");
      setDatepickerValue(parsed.toDate().toDateString());
      setMonth(parsed.month());
      setYear(parsed.year());
    } else {
      initDate();
    }
  }, [dateValue]);

  const initDate = () => {
    const today = new Date();
    setMonth(today.getMonth());
    setYear(today.getFullYear());
    setDatepickerValue(moment(today).format("YYYY-MM-DD"));
  };

  // Function to navigate between months and handle year transitions
  const handleNavigateMonthYear = (direction: "prev" | "next") => {
    let newMonth = month;
    let newYear = year;

    if (direction === "prev") {
      if (month === 0) {
        newMonth = 11;
        newYear -= 1;
      } else {
        newMonth -= 1;
      }
    } else {
      if (month === 11) {
        newMonth = 0;
        newYear += 1;
      } else {
        newMonth += 1;
      }
    }
    setMonth(newMonth);
    setYear(newYear);
  };

  // Function to submit the selected date
  const submitDate = () => {
    const selectedDate = moment(
      datepickerValue,
      "ddd MMM DD YYYY HH:mm:ss [GMT]ZZ",
    );
    if (!selectedDate.isValid()) {
      return;
    }
    const formattedDate = selectedDate?.format("YYYY-MM-DD");
    setDate(formattedDate);
    setShowDatepicker(false);
  };

  // Check if the given date is today
  const isToday = (date: number): boolean => {
    const today = new Date();
    const d = new Date(year, month, date);
    return today.toDateString() === d.toDateString();
  };

  // Set the selected date value
  const getDateValue = (date: number) => {
    const selectedDate = new Date(year, month, date);
    setDatepickerValue(selectedDate.toDateString());
    if (dateRef.current) {
      dateRef.current.value = `${selectedDate.getFullYear()}-${("0" + (selectedDate.getMonth() + 1)).slice(-2)}-${("0" + selectedDate.getDate()).slice(-2)}`;
    }
  };

  // Calculate the number of days in the current month and the blank days for padding
  const getNoOfDays = () => {
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const dayOfWeek = new Date(year, month).getDay();
    const blankdaysArray = [];
    for (let i = 1; i <= dayOfWeek; i++) {
      blankdaysArray.push(i);
    }
    const daysArray = [];
    for (let i = 1; i <= daysInMonth; i++) {
      daysArray.push(i);
    }
    setBlankdays(blankdaysArray);
    setNoOfDays(daysArray);
  };

  // Update the days when the month or year changes
  useEffect(() => {
    getNoOfDays();
    setYearMonth(`${MONTH_NAMES[month]} ${year}`);
  }, [month, year]);

  // Initialize the date picker on component mount
  useEffect(() => {
    // initDate();
    getNoOfDays();
  }, []);

  const ref = useClickOutside(() => setShowDatepicker(false));

  const clearDate = () => {
    setDate("");
    setDatepickerValue("");
  };

  const handleChangeYearAndMonth = (yearMonthvalue: string) => {
    const [monthName, yearStr] = yearMonthvalue.split(" ");
    setMonth(MONTH_NAMES.indexOf(monthName));
    setYear(Number(yearStr));
  };

  // const selectedDate = new Date(year, month, date).toISOString().split('T')[0];

  return (
    <div className={`${width ? `w-[${width}]` : "w-full"}`}>
      {label && (
        <label className={`text-[#6F6464] text-[12px]  font-semibold`}>
          {label}
        </label>
      )}

      <div
        ref={ref}
        className={`relative  ${width ? `w-[${width}]` : "w-full"}`}
      >
        <input
          type="hidden"
          className={`${disabled ? "cursor-not-allowed" : "cursor-pointer"} `}
          disabled={disabled}
          name="date"
          ref={dateRef}
        />
        <input
          type="text"
          readOnly
          value={dateValue ? dateValue : ""}
          onClick={() => setShowDatepicker(!showDatepicker)}
          className={`w-full placeholder:text-[#DDD] text-[#303030]  text-[12px] ${color ? color : "bg-white-primary"} ${disabled ? "cursor-not-allowed" : "cursor-pointer"} ${border ? "border" : ""} rounded-md px-3 ${height ? height : "h-[45px]"} text-[12px] font-normal ${dateValue ? "text-black-primary" : "#999"}  p-2  focus:outline-none`}
          placeholder={placeHolder ? placeHolder : "Select date"}
          disabled={disabled}
        />
        <div
          className={`absolute ${disabled ? "cursor-not-allowed" : "cursor-pointer"} ${height ? "-top-[8px]" : "-top-0.5"} right-0 px-3 py-4 `}
        >
          {dateValue ? (
            <CloseCircleFilled
              onClick={clearDate}
              className="text-[#999] text-[13px] cursor-pointer"
            />
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
            >
              <path
                d="M4.03979 15.9702C3.68547 15.9702 3.38383 15.8457 3.13488 15.5967C2.88594 15.3478 2.76147 15.0461 2.76147 14.6918V4.77114C2.76147 4.4168 2.88594 4.11516 3.13488 3.86623C3.38383 3.61728 3.68604 3.4928 4.0415 3.4928H5.1557V2.44349C5.1557 2.29674 5.20856 2.17052 5.31429 2.06482C5.42001 1.95911 5.54717 1.90625 5.69577 1.90625C5.84437 1.90625 5.9712 1.95911 6.07625 2.06482C6.18129 2.17052 6.23381 2.29674 6.23381 2.44349V3.4928H11.7953V2.42908C11.7953 2.28641 11.8467 2.16361 11.9495 2.06068C12.0524 1.95773 12.1759 1.90625 12.3202 1.90625C12.4645 1.90625 12.588 1.95773 12.6906 2.06068C12.7933 2.16361 12.8446 2.28641 12.8446 2.42908V3.4928H13.9588C14.3142 3.4928 14.6165 3.61728 14.8654 3.86623C15.1143 4.11516 15.2388 4.4168 15.2388 4.77114V14.6918C15.2388 15.0461 15.1143 15.3478 14.8654 15.5967C14.6165 15.8457 14.3148 15.9702 13.9605 15.9702H4.03979ZM4.0415 14.9209H13.9588C14.0165 14.9209 14.0694 14.8969 14.1174 14.8488C14.1655 14.8007 14.1896 14.7478 14.1896 14.6901V7.75769H3.81072V14.6901C3.81072 14.7478 3.83476 14.8007 3.88284 14.8488C3.93092 14.8969 3.98381 14.9209 4.0415 14.9209ZM6.0398 10.2637C5.89437 10.2637 5.77028 10.2123 5.66752 10.1095C5.56476 10.0066 5.51337 9.8831 5.51337 9.7388C5.51337 9.5945 5.56476 9.47102 5.66752 9.36837C5.77028 9.26574 5.89437 9.21442 6.0398 9.21442H11.9641C12.1067 9.21442 12.2295 9.26591 12.3325 9.3689C12.4354 9.4719 12.4869 9.59476 12.4869 9.73749C12.4869 9.883 12.4354 10.0071 12.3325 10.1097C12.2295 10.2123 12.1067 10.2637 11.9641 10.2637H6.0398ZM6.0398 13.2182C5.89437 13.2182 5.77028 13.1668 5.66752 13.064C5.56476 12.9612 5.51337 12.8377 5.51337 12.6934C5.51337 12.5491 5.56476 12.4256 5.66752 12.3229C5.77028 12.2203 5.89437 12.169 6.0398 12.169H9.74437C9.88703 12.169 10.0098 12.2205 10.1128 12.3235C10.2157 12.4265 10.2672 12.5493 10.2672 12.6921C10.2672 12.8376 10.2157 12.9616 10.1128 13.0643C10.0098 13.1669 9.88703 13.2182 9.74437 13.2182H6.0398Z"
                fill="#6F6464"
              />
            </svg>
          )}
        </div>
        {showDatepicker && (
          <div
            onWheel={(e) => {
              e.stopPropagation();
            }}
            onTouchMove={(e) => {
              e.stopPropagation();
            }}
            className={`bg-white-primary border-b-2 overflow-y-auto custom-scrollbar overscroll-contain mt-[2px] bg-white w-[290px] transition-all duration-100 shadow-lg  z-50  rounded-lg ${showtoptopicker ? showtoptopicker : "top-12"} p-5 absolute ${showLeft ? "right-0" : "left-0"}`}
          >
            <div className="flex justify-between items-center mb-6">
              <CustomYearMonthPicker
                setYearMonth={handleChangeYearAndMonth}
                yearMonthvalue={yearMonth}
                dropdownHeight="h-[290px]"
                dropdownShow="-left-[3px] "
                stateEmpty={false}
                monthRangeShow={
                  <h1 className="text-[18px] cursor-pointer font-semibold text-[#2D65F2]">
                    {MONTH_NAMES[month]} {year}
                  </h1>
                }
              />

              <div className="flex-center gap-3">
                <button
                  type="button"
                  className={`transition ease-in-out duration-100 inline-flex cursor-pointer hover:bg-gray-200 p-1 rounded-full `}
                  onClick={() => handleNavigateMonthYear("prev")}
                >
                  <svg
                    className="h-5 w-5 text-gray-500 inline-flex"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                <button
                  type="button"
                  className={`transition ease-in-out duration-100 inline-flex cursor-pointer hover:bg-gray-200 p-1 rounded-full`}
                  onClick={() => handleNavigateMonthYear("next")}
                >
                  <svg
                    className="h-5 w-5 text-gray-500 inline-flex"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div className="flex justify-between mb-4 -mx-1">
              {DAYS.map((day, index) => (
                <div key={index} className="">
                  <div className="text-[#999] font-normal text-center text-[12px]">
                    {day}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap -mx-1">
              {blankdays.map((_, index) => (
                <div
                  key={index}
                  style={{ width: "14.28%" }}
                  className="text-center border  border-transparent"
                ></div>
              ))}
              {no_of_days?.map((date, dateIndex) => (
                <div
                  key={dateIndex}
                  style={{ width: "14.28%" }}
                  className="px-1 mb-1"
                >
                  <div
                    id={`date-${date}`}
                    onClick={() => getDateValue(date)}
                    className={`cursor-pointer text-[#303030] text-center w-[36px] h-[36px] flex items-center justify-center font-normal text-[12px] rounded-full ${
                      isToday(date)
                        ? `bg-[#2D65F2] text-white`
                        : datepickerValue ===
                              new Date(year, month, date).toDateString() ||
                            moment(dateValue).format("DD-MM-YYYY") ===
                              moment(new Date(year, month, date)).format(
                                "DD-MM-YYYY",
                              )
                          ? "border border-[#2D65F2]  text-black"
                          : "hover:bg-[#F6F6F6]"
                    }`}
                  >
                    <p className="mt-[1.5px]">{date}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex-center mt-3 w-full">
              <button
                onClick={() => setShowDatepicker(false)}
                className="w-full cursor-pointer text-[12px] font-semibold text-[#999]"
              >
                Close
              </button>
              <button
                disabled={!datepickerValue}
                onClick={submitDate}
                className={` ${!datepickerValue ? "disabledButton w-full h-[33px]" : "bg-[#2D65F2] py-[6px] rounded-md text-white text-[12px] font-semibold w-full"} `}
              >
                Select
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomDatePicker;
